import { useGetFonts } from "@/hooks/useGetFonts";
import { axiosGet } from "@/services/axiosService";
import React, { useEffect, useState } from "react";
import {
  AffordableSvg,
  BalancedSvg,
  BasicSvg,
  CodingSvg,
  ConsistentSvg,
  ContextSvg,
  FastIcon,
  GeneralistSvg,
  LightweightSvg,
  MutltimodalSvg,
  OpenSourceSvg,
  QualitySvg,
  ReasonSvg,
  ReliableSvg,
  StructuredSvg,
  TechnicalSvg,
  ToolsSvg,
  UltraFastSvg,
} from "@/assets/AiBotAssets";

const botTone = [
  {
    tone: "Friendly",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/pd1LNRxpSHeB02nm8aEg___.svg",
  },
  {
    tone: "Professional",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/AZQa5wYoQamwLLnIU6q9___.svg",
  },
  {
    tone: "Informative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/guaQjAQNORaAaXrFGAPa___.svg",
  },
  // {
  //   tone: "Urgent",
  //   emoji: "https://dlvkyia8i4zmz.cloudfront.net/c2KxnEmfRc2boafchSxh__.svg",
  // },
  // {
  //   tone: "Humorous",
  //   emoji: "https://dlvkyia8i4zmz.cloudfront.net/ooQLXQp5RVWFRPEzOoSl___.svg",
  // },
  {
    tone: "Apologetic",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/m2HtWHK1RkaZdW5OkQhz___.svg",
  },
  {
    tone: "Appreciative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/gVdh1cfHR9643L1Iml8R___.svg",
  },
  {
    tone: "Assertive",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/aGbxSxTWXOfO6QDjgiAv___.svg",
  },
  {
    tone: "Authoritative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/IvQqJeZT2XMQlzqdhTOg___.svg",
  },
  {
    tone: "Witty",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/mY7ejzDRMWxhYOAhiZYk_Witty.svg",
  },
  {
    tone: "Sarcastic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/RUqX2aKVS7Or3Qiw5rdw_Sarcastic.svg",
  },
  {
    tone: "Optimistic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/zjNDsl96SFOLEvqsaROS_Optimistic.svg",
  },
  {
    tone: "Empathetic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/HaLesr4QTSsdYtHpXHrS_Empathetic_emoji.svg",
  },
  {
    tone: "Informal",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/62q36oo6Ske4AikzuMuH_informal.png",
  },
];

export const APP_SUMO_USAGE_LIMIT_BUILDER = 50;
export const NEW_USER_INSTRUCTION = `
### 1. Train Your Agent with Data
- Add website, YouTube, sitemap links, or upload files (MP4, PDF, CSV, etc.) to train your agent.
- Connect apps like Notion, Salesforce, and Google Drive for diverse data sources.

### 2. Design & Configure Your Agent
- Set up your agent's identity, behavior, chat enhancements, nudges, and customize its role and AI model.
- Define how your agent interacts and appears to users.

### 3. Deploy & Integrate
- Easily deploy your agent on websites, WhatsApp, Messenger, Telegram, and Instagram.
- Integrate with live chat platforms (Intercom, Zendesk) and 5000+ apps via Zapier.

### 4. Monitor, Use the “Improve Your Agent Section”, and Analyze
- Track key metrics like resolution, sentiment, user demographics, and popular topics.
- Use the Improve Your Agent and Analyze sections to measure performance and continuously optimize your agent.

### 5. Watch Video Instructions
- Refer to the video tutorials [here](https://support.chatbotbuilder.net/) for step-by-step guidance on training, configuring, and managing your chatbot to ensure smooth setup and effective use.

<links-cbb>
<li>https://support.chatbotbuilder.net/</li>
</links-cbb>
`;

const language = [
  "English (US)",
  "English (UK)",
  "German",
  "Spanish",
  "French",
  "Italian",
  "Portuguese",
  "Polish",
  "Ukrainian",
  "Somali",
  "Afrikaans",
  "Azerbaijani",
  "Indonesian",
  "Malaysian Malay",
  "Malay",
  "Javanese",
  "Sundanese",
  "Bosnian",
  "Catalan",
  "Czech",
  "Chichewa",
  "Welsh",
  "Danish",
  "Estonian",
  "Esperanto",
  "Basque",
  "Irish",
  "Galician",
  "Croatian",
  "Xhosa",
  "Zulu",
  "Icelandic",
  "Swahili",
  "Haitian Creole",
  "Kurdish",
  "Latin",
  "Latvian",
  "Luxembourgish",
  "Lithuanian",
  "Hungarian",
  "Malagasy",
  "Maltese",
  "Maori",
  "Dutch",
  "Norwegian",
  "Uzbek",
  "Romanian",
  "Sesotho",
  "Albanian",
  "Slovak",
  "Slovenian",
  "Finnish",
  "Swedish",
  "Tagalog",
  "Tatar",
  "Turkish",
  "Vietnamese",
  "Yoruba",
  "Greek",
  "Belarusian",
  "Bulgarian",
  "Kyrgyz",
  "Kazakh",
  "Macedonian",
  "Mongolian",
  "Russian",
  "Serbian",
  "Tajik",
  "Georgian",
  "Armenian",
  "Yiddish",
  "Hebrew",
  "Uyghur",
  "Urdu",
  "Arabic",
  "Pashto",
  "Persian",
  "Nepali",
  "Marathi",
  "Hindi",
  "Bengali",
  "Punjabi",
  "Gujarati",
  "Oriya",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Sinhala",
  "Thai",
  "Lao",
  "Burmese",
  "Khmer",
  "Korean",
  "Chinese",
  "Traditional Chinese",
  "Japanese",
];
// const models = [
//   "gpt-3.5-turbo",
//   "gpt-3.5-turbo-0301",
//   "gpt-3.5-turbo-0613",
//   "gpt-3.5-turbo-16k",
//   "gpt-3.5-turbo-16k-0613",
//   "gpt-4",
//   "gpt-4-0613",
//   "gpt-4-32k",
//   "gpt-4-32k-0613",
// ];

//  const models = [
//   "4-turbo",
//   "4o",
//   "4o-mini",
//   "3.5-turbo",
//   "3.5-sonnet",
//   "3-sonnet",
//   "3-haiku",
//   "3-opus",
//   "none of these"
// ];

const openAiModels = [
  // {
  //   // b
  //   name: "gpt-5.6-sol",
  //   displayName: "OpenAI GPT 5.6 Sol",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  //   categories: ["Think", "Actions", "Quality"],
  //   provider: "openai",
  // },
  {
    // b
    name: "gpt-5.6-terra",
    displayName: "OpenAI GPT 5.6 Terra (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5.6-luna",
    displayName: "OpenAI GPT 5.6 Luna",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5.5",
    displayName: "OpenAI GPT 5.5",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5.4",
    displayName: "OpenAI GPT 5.4",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5.4-mini",
    displayName: "OpenAI GPT 5.4 Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Affordable", "Lightweight"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5.4-nano",
    displayName: "OpenAI GPT 5.4 Nano",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Basic", "Fast"],
    provider: "openai",
  },
  {
    // a
    name: "gpt-5",
    displayName: "OpenAI GPT 5",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality"],
    provider: "openai",
  },
  {
    // b
    name: "gpt-5-mini",
    displayName: "OpenAI GPT 5 Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Affordable", "Fast", "Think"],
    provider: "openai",
  },
  {
    //a
    name: "4.1",
    displayName: "OpenAI GPT 4.1",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Quality", "Generalist"],
    provider: "openai",
  },
  {
    //a
    name: "4o",
    displayName: "OpenAI GPT 4o",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Generalist", "Quality", "Actions"],
    provider: "openai",
  },
  {
    //b
    name: "4.1-nano",
    displayName: "OpenAI GPT 4.1 Nano",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Lightweight", "Basic", "Fast"],
    provider: "openai",
  },
  {
    //b
    name: "4o-mini",
    displayName: "OpenAI GPT 4o Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Fast", "Affordable", "Lightweight"],
    provider: "openai",
  },
  {
    //a
    name: "4.1-mini",
    displayName: "OpenAI GPT 4.1 mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Fast", "Basic"],
    provider: "openai",
  },
];

const claudeModels = [
  // {
  //   name: "4.7-opus",
  //   displayName: "Anthropic Claude Opus 4.7",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  //   categories: ["Quality", "Actions", "Reliable"],
  //   provider: "anthropic",
  // },
  // {
  //   name: "4.8-opus",
  //   displayName: "Anthropic Claude Opus 4.8",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  //   categories: ["Quality", "Actions", "Reliable"],
  //   provider: "anthropic",
  // },
  {
    name: "5-sonnet",
    displayName: "Anthropic Claude 5 Sonnet (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Actions", "Reliable", "Context"],
    provider: "openrouter",
    uiProvider: "anthropic",
  },
  {
    name: "4.6-sonnet",
    displayName: "Anthropic Claude 4.6 Sonnet",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Actions", "Reliable", "Fast"],
    provider: "anthropic",
  },
  {
    name: "4.5-sonnet",
    displayName: "Anthropic Claude 4.5 Sonnet",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Actions", "Reliable", "Fast"],
    provider: "anthropic",
  },
  {
    name: "4.5-haiku",
    displayName: "Anthropic Claude 4.5 Haiku",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Fast", "Actions", "Affordable"],
    provider: "anthropic",
  },
  // {
  //   name: "4-sonnet",
  //   displayName: "Anthropic Claude 4 Sonnet",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  //   categories: ["Quality", "Actions", "Reliable"],
  //   provider: "anthropic",
  // },
];

const geminiModels = [
  // {
  //   name: "Gemini 3.1 Pro Preview",
  //   displayName: "Google Gemini 3.1 Pro Preview",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  //   categories: ["Think", "Actions", "Generalist"],
  //   provider: "google",
  // },
  // {
  //   name: "Gemini 3 Flash Preview",
  //   displayName: "Google Gemini 3 Flash Preview",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  //   categories: ["Fast", "Think", "Affordable"],
  //   provider: "google",
  // },
  // {
  //   name: "Gemini 3.1 Flash Lite Preview",
  //   displayName: "Google Gemini 3.1 Flash Lite Preview",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  //   categories: ["Fast", "Think", "Affordable"],
  //   provider: "google",
  // },
  {
    //b
    name: "Gemini 3.6 Flash",
    displayName: "Google Gemini 3.6 Flash",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Think", "Affordable"],
    provider: "google",
  },
  {
    //b
    name: "Gemini 3.5 Flash Lite",
    displayName: "Google Gemini 3.5 Flash Lite",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Affordable", "Lightweight"],
    provider: "google",
  },
  {
    //a
    name: "Gemini 2.5 Pro",
    displayName: "Google Gemini 2.5 Pro (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Think", "Actions", "Generalist"],
    provider: "google",
  },
  {
    //b
    name: "Gemini 2.5 Flash",
    displayName: "Google Gemini 2.5 Flash",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Affordable"],
    provider: "google",
  },
  {
    //b
    name: "Gemini 2.5 Flash Lite",
    displayName: "Google Gemini 2.5 Flash Lite",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Affordable", "Lightweight"],
    provider: "google",
  },
];

const grokModels = [
  {
    name: "x-ai/grok-4.3",
    displayName: "X Grok 4.3 (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["Think", "Quality", "Context"],
    provider: "openrouter",
    uiProvider: "grok",
  },
];

const deepSeekModels = [
  // {
  //   name: "deepseek-r1-0528",
  //   displayName: "DeepSeek R1 (0528)",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
  //   categories: ["OpenSource", "Think", "Quality"],
  //   provider: "deepseek",
  // },
  {
    name: "deepseek-v4-pro",
    displayName: "DeepSeek V4 Pro",
    url: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
    categories: ["OpenSource", "Think", "Quality"],
    provider: "deepseek",
  },
  {
    name: "deepseek-v4-flash",
    displayName: "DeepSeek V4 Flash",
    url: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
    categories: ["OpenSource", "Fast", "Affordable"],
    provider: "deepseek",
  },
  {
    name: "deepseek-chat",
    displayName: "DeepSeek Chat V3",
    url: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
    categories: ["OpenSource", "Affordable", "Basic"],
    provider: "deepseek",
  },
];

const miniMaxModels = [
  {
    name: "minimax/minimax-m2.7",
    displayName: "MiniMax M2.7 (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/NdKB4E20RUq8WwO1F0qX_minimax_color.svg",
    categories: ["Think", "Affordable", "Generalist"],
    provider: "openrouter",
    uiProvider: "minimax",
  },
  {
    name: "minimax/minimax-m2.5",
    displayName: "MiniMax M2.5",
    url: "https://dlvkyia8i4zmz.cloudfront.net/NdKB4E20RUq8WwO1F0qX_minimax_color.svg",
    categories: ["Think", "Affordable", "Generalist"],
    provider: "openrouter",
    uiProvider: "minimax",
  },
];

const moonshotModels = [
  {
    name: "moonshotai/kimi-k3",
    displayName: "Moonshot Kimi K3 (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/U0LqTJURC62XA8nbdW1A_moonshot.svg",
    categories: ["Coding", "Think", "Affordable"],
    provider: "openrouter",
    uiProvider: "moonshotai",
  },
  {
    name: "moonshotai/kimi-k2.6",
    displayName: "Moonshot Kimi K2.6",
    url: "https://dlvkyia8i4zmz.cloudfront.net/U0LqTJURC62XA8nbdW1A_moonshot.svg",
    categories: ["Coding", "Think", "Affordable"],
    provider: "openrouter",
    uiProvider: "moonshotai",
  },
  {
    name: "moonshotai/kimi-k2.5",
    displayName: "Moonshot Kimi K2.5",
    url: "https://dlvkyia8i4zmz.cloudfront.net/U0LqTJURC62XA8nbdW1A_moonshot.svg",
    categories: ["Coding", "Think", "Affordable"],
    provider: "openrouter",
    uiProvider: "moonshotai",
  },
];

const zAiModels = [
  {
    name: "glm-5.2",
    displayName: "Z.ai GLM 5.2 (Recommended)",
    url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
    categories: ["Coding", "Quality", "Context"],
    provider: "openrouter",
    uiProvider: "z-ai",
  },
  // {
  //   name: "glm-5.1",
  //   displayName: "Z.ai GLM 5.1",
  //   url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  //   categories: ["Coding", "Think", "Quality"],
  //   provider: "openrouter",
  //   uiProvider: "z-ai",
  // },
  // {
  //   name: "glm-5-turbo",
  //   displayName: "Z.ai GLM 5 Turbo",
  //   url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  //   categories: ["Fast", "Coding", "Affordable"],
  //   provider: "openrouter",
  //   uiProvider: "z-ai",
  // },
  // {
  //   name: "glm-4.7",
  //   displayName: "Z.ai GLM 4.7",
  //   url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  //   categories: ["Coding", "Think", "Affordable"],
  //   provider: "openrouter",
  //   uiProvider: "z-ai",
  // },
  // {
  //   name: "glm-4.6",
  //   displayName: "Z.ai GLM 4.6",
  //   url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  //   categories: ["Coding", "Think", "Affordable"],
  //   provider: "openrouter",
  //   uiProvider: "z-ai",
  // },
  // {
  //   name: "glm-4.5",
  //   displayName: "Z.ai GLM 4.5",
  //   url: "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  //   categories: ["Coding", "Think", "Affordable"],
  //   provider: "openrouter",
  //   uiProvider: "z-ai",
  // },
];

const groqModels = [
  //preview models
  // {
  //   name: "kimi-k2",
  //   displayName: "Moonshot Kimi K2 (Recommended)",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
  //   categories: ["OpenSource", "Coding", "Affordable"],
  //   provider: "groq",
  // },
  // {
  //   name: "llama-4-maverick-17b-128e-instruct",
  //   displayName: "Meta LLaMA 4 Maverick",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
  //   categories: ["Think", "Quality", "Context"],
  // },

  // --- Production Models ---
  {
    name: "oss-120b",
    displayName: "OpenAI GPT-OSS 120B (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
    categories: ["OpenSource", "Think", "Affordable"],
    provider: "groq",
  },
];

const AUTO_MODEL = {
  name: "auto",
  displayName: "Auto",
  url: "https://dlvkyia8i4zmz.cloudfront.net/47zjT8z5QPydTiEVuYfN_Frame__8___2_.svg",
  categories: ["Quality", "Fast", "Actions", "Reliable"],
};

export const MAESTRO_AUTO_MODE_OPTIONS = [
  {
    value: "lowCost",
    label: "Cost Saver",
    modelName: "auto-low-cost",
    categories: ["Affordable", "Reliable", "Basic"],
  },
  {
    value: "balanced",
    label: "Balanced",
    modelName: "auto-balanced",
    categories: ["Quality", "Actions", "Reliable"],
  },
  {
    value: "quality",
    label: "Quality",
    modelName: "auto-quality",
    categories: ["Quality", "Consistent", "Actions", "Reliable"],
  },
] as const;

export type MaestroAutoMode =
  (typeof MAESTRO_AUTO_MODE_OPTIONS)[number]["value"];

const AUTO_MODE_VALUE_TO_NAME: Record<MaestroAutoMode, string> = {
  lowCost: "auto-low-cost",
  balanced: "auto-balanced",
  quality: "auto-quality",
};

const AUTO_MODE_NAME_TO_VALUE: Record<string, MaestroAutoMode> = {
  "auto-lowCost": "lowCost",
  "auto-low-cost": "lowCost",
  "auto-low_cost": "lowCost",
  "auto-balanced": "balanced",
  "auto-quality": "quality",
};

export const normalizeAutoMode = (mode?: string): MaestroAutoMode => {
  if (mode === "lowCost" || mode === "balanced" || mode === "quality") {
    return mode;
  }
  if (
    mode === "speed" ||
    mode === "low_cost" ||
    mode === "low-cost" ||
    mode === "lowcost"
  )
    return "lowCost";
  return "balanced";
};

export const getAutoModeFromModelName = (
  modelName?: string,
): MaestroAutoMode | undefined => {
  if (!modelName) return undefined;
  if (modelName === "auto") return "balanced";
  return AUTO_MODE_NAME_TO_VALUE[modelName];
};

export const getAutoModeModelName = (mode?: string): string => {
  const normalizedMode = normalizeAutoMode(mode);
  return AUTO_MODE_VALUE_TO_NAME[normalizedMode];
};

export const isAutoFamilyModelName = (modelName?: string): boolean =>
  !!modelName && (modelName === "auto" || !!AUTO_MODE_NAME_TO_VALUE[modelName]);

export const resolveModelNameForSelection = (modelName?: string): string =>
  isAutoFamilyModelName(modelName) ? "auto" : modelName || "";

export const AUTO_MODE_MODELS = MAESTRO_AUTO_MODE_OPTIONS.map((option) => ({
  ...AUTO_MODEL,
  name: option.modelName,
  displayName: option.label,
  autoMode: option.value,
}));

const thinkingModels = [
  "gpt-5",
  "gpt-5.4",
  "gpt-5.4-pro",
  "gpt-5.4-mini",
  "gpt-5.4-nano",
  // "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-5.5",
  "gpt-5-mini",
  "3.7-sonnet",
  "5-sonnet",
  "4-sonnet",
  "4.5-sonnet",
  "4.5-haiku",
  "4.6-sonnet",
  "2.5-pro",
  "2.5-flash",
  "Gemini 2.5 Pro",
  "Gemini 2.5 Flash",
  "Gemini 2.5 Flash Lite",
  // "Gemini 3 Pro Preview",
  // "Gemini 3.1 Pro Preview",
  // "Gemini 3 Flash Preview",
  // "Gemini 3.1 Flash Lite Preview",
  "Gemini 3.5 Flash",
  "Gemini 3.6 Flash",
  "Gemini 3.5 Flash Lite",
  "o1",
  "o1-mini",
  "o3-mini",
  "deepseek-r1",
  "gpt-oss-120b",
  "oss-120b",
  "grok-4.3",
  "grok-3",
  "grok-3-fast",
  "grok-3-mini",
  "grok-3-mini-fast",
  "minimax/minimax-m2.5",
  "minimax/minimax-m2.7",
  // "4.8-opus",
  // "4.7-opus",
  "deepseek-v4-pro",
  "deepseek-v4-flash",
  "kimi-k2",
  "moonshotai/kimi-k2.6",
  "moonshotai/kimi-k2.5",
  "glm-5.2",
  // "glm-5.1",
  // "glm-5-turbo",
  // "glm-4.7",
  // "glm-4.6",
  // "glm-4.5",
];

export const categoryIcons: Record<string, React.FC> = {
  Fast: FastIcon,
  Affordable: AffordableSvg,
  Balanced: BalancedSvg,
  Basic: BasicSvg,
  Coding: CodingSvg,
  Complex: BasicSvg,
  Consistent: ConsistentSvg,
  Context: ContextSvg,
  Generalist: GeneralistSvg,
  Lightweight: LightweightSvg,
  Multimodal: MutltimodalSvg,
  Quality: QualitySvg,
  Think: ReasonSvg,
  Reliable: ReliableSvg,
  Technical: TechnicalSvg,
  Actions: ToolsSvg,
  UltraFast: UltraFastSvg,
  OpenSource: OpenSourceSvg,
};

const models = [
  AUTO_MODEL,
  ...claudeModels,
  ...openAiModels,
  ...geminiModels,
  ...moonshotModels,
  ...miniMaxModels,
  ...zAiModels,
  ...grokModels,
  ...groqModels,
  ...deepSeekModels,
];

const flags = {
  Pashto: "AF",
  German: "DE",
  Spanish: "ES",
  French: "FR",
  Italian: "IT",
  Portuguese: "PT",
  Polish: "PL",
  Ukrainian: "UA",
  Somali: "SO",
  English: "GB",
  "English (US)": "US",
  "English (UK)": "GB",
  Afrikaans: "ZA",
  Azerbaijani: "AZ",
  Indonesian: "ID",
  "Malaysian Malay": "MY",
  Malay: "BN",
  Javanese: "ID",
  Sundanese: "ID",
  Bosnian: "BA",
  Catalan: "AD",
  Czech: "CZ",
  Chichewa: "MW",
  Welsh: "GB",
  Danish: "DK",
  Estonian: "EE",
  Esperanto: "BR",
  Basque: "ES",
  Irish: "IE",
  Galician: "ES",
  Croatian: "HR",
  Xhosa: "ZA",
  Zulu: "ZA",
  Icelandic: "IS",
  Swahili: "TZ",
  "Haitian Creole": "HT",
  Kurdish: "TR",
  Latin: "VA",
  Latvian: "LV",
  Luxembourgish: "LU",
  Lithuanian: "LT",
  Hungarian: "HU",
  Malagasy: "MG",
  Maltese: "MT",
  Maori: "NZ",
  Dutch: "NL",
  Norwegian: "NO",
  Uzbek: "UZ",
  Romanian: "RO",
  Sesotho: "ZA",
  Albanian: "AL",
  Slovak: "SK",
  Slovenian: "SI",
  Finnish: "FI",
  Swedish: "SE",
  Tagalog: "PH",
  Tatar: "RU",
  Turkish: "TR",
  Vietnamese: "VN",
  Yoruba: "NG",
  Greek: "GR",
  Belarusian: "BY",
  Bulgarian: "BG",
  Kyrgyz: "KG",
  Kazakh: "KZ",
  Macedonian: "MK",
  Mongolian: "MN",
  Russian: "RU",
  Serbian: "RS",
  Tajik: "TJ",
  Georgian: "GE",
  Armenian: "AM",
  Yiddish: "IL",
  Hebrew: "IL",
  Uyghur: "CN",
  Urdu: "PK",
  Arabic: "EG",
  Persian: "IR",
  Nepali: "NP",
  Marathi: "IN",
  Hindi: "IN",
  Bengali: "IN",
  Punjabi: "IN",
  Gujarati: "IN",
  Oriya: "IN",
  Tamil: "IN",
  Telugu: "IN",
  Kannada: "IN",
  Malayalam: "IN",
  Sinhala: "LK",
  Thai: "TH",
  Lao: "LA",
  Burmese: "MM",
  Khmer: "KH",
  Korean: "KR",
  Chinese: "CN",
  "Traditional Chinese": "CN",
  Japanese: "JP",
};
const usageLimit = [25, 50, 100, 200];
const replyLength = [
  {
    id: "auto",
    title: "Auto",
    description: "Let AI decide the right length based on the context",
  },
  {
    id: "short",
    title: "Short & Concise",
    description: "1-2 sentences (20-50 words)",
  },
  {
    id: "medium",
    title: "Standard",
    description: "2-4 sentences (50-120 words)",
  },
  {
    id: "long",
    title: "Detailed",
    description: "4-10 sentences (120-350 words)",
  },
  {
    id: "custom",
    title: "Custom",
    description: "Set a custom word limit, up to 1000",
  },
];

const suggestions = [
  { label: "Email", question: "Please provide your email address?" },
  { label: "Phone", question: "Please provide your phone number?" },
  { label: "Name", question: "Please provide your full name?" },
  { label: "Company", question: "Please provide your company name?" },
  { label: "Job", question: "Please provide your job title?" },
  { label: "Address", question: "Please provide your current address?" },
];

const lightColors = [
  "#706AFF",
  "#DFA836",
  "#5CCF3D",
  "#E75A5A",
  "#4DA0E3",
  "#446CB1",
];
const darkColors = [
  "#A7A4FF",
  "#FFDC94",
  "#92D381",
  "#ED8888",
  "#83BAE7",
  "#6685B9",
];

const themeColors = [
  { id: 1, light: "#A7A4FF", dark: "#F64F9E" },
  { id: 2, light: "#FFDC94", dark: "#706AFF" },
  { id: 3, light: "#92D381", dark: "#FF5257" },
  { id: 4, light: "#ED8888", dark: "#F6821B" },
  { id: 5, light: "#83BAE7", dark: "#FCB826" },
];

const gradientColors = [
  {
    id: 1,
    gradient: "linear-gradient(180deg, #FF930F 0%, #FFF95B 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 2,
    gradient: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    btnBgColor: "linear-gradient(90deg, #FF787E 0%, #E3232C 100%)",
    btnTextColor: "#fff",
  },
  {
    id: 3,
    gradient: "linear-gradient(180deg, #FF0F7B 0%, #FFD3E7 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 4,
    gradient: "linear-gradient(180deg, #0061FF 0%, #E5EFFF 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 5,
    gradient: "linear-gradient(180deg, #9D1760 0%, #FFD7ED 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 6,
    gradient: "linear-gradient(180deg, #FF31D2 0%, #FFF 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 7,
    gradient: "linear-gradient(180deg, #C196FF 0%, #9868DD 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 8,
    gradient: "linear-gradient(180deg, #020021 0%, #808080 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 9,
    gradient: "linear-gradient(180deg, #083D77 0%, #3671B1 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 10,
    gradient: "linear-gradient(180deg, #237131 0%, #69F081 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
];

// export const fetchFontTypes = async () => {
//   try {
//     const response: any = await axiosGet("/users/getFonts");

//     if (!response.data) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const fontsData = await response?.data?.data;
//     const mappedFonts = fontsData
//       .filter((font) => font.isActive)
//       .map((font) => ({
//         fontURL: font.url,
//         fontFamily: font.name,
//         displayName: font.name,
//       }))
//       .sort((a, b) => a.displayName.localeCompare(b.displayName));

//     return mappedFonts;
//   } catch (error) {
//     console.error("Error fetching fonts:", error);

//     return [
//       {
//         fontURL:
//           "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap",
//         fontFamily: "Inter",
//         displayName: "Inter",
//       },
//       {
//         fontURL:
//           "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap",
//         fontFamily: "Roboto",
//         displayName: "Roboto",
//       },
//     ];
//   }
// };

export const loadFontCSS = async (fontConfig) => {
  const { fontURL, fontFamily, displayName } = fontConfig;

  try {
    const linkId = `font-link-${fontFamily.replace(/\s+/g, "-").toLowerCase()}`;
    let linkElement: any = document.getElementById(linkId);

    if (!linkElement) {
      linkElement = document.createElement("link");
      linkElement.id = linkId;
      linkElement.rel = "stylesheet";
      linkElement.href = fontURL;
      document.head.appendChild(linkElement);

      return new Promise((resolve, reject) => {
        linkElement.onload = () => {
          resolve(true);
        };
        linkElement.onerror = () => {
          console.error(`❌ Font failed: ${displayName}`);
          reject(false);
        };
      });
    }
    return true;
  } catch (error) {
    console.error(`❌ Font loading error for ${displayName}:`, error);
    return false;
  }
};

// Enhanced useFontLoader hook with preloading capabilities
export const useFontLoader = () => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [loadingFonts, setLoadingFonts] = useState(new Set());
  const [failedFonts, setFailedFonts] = useState(new Set());

  const loadFont = React.useCallback(
    async (fontConfig, retries = 1) => {
      if (loadedFonts.has(fontConfig.fontFamily)) {
        return true;
      }

      if (loadingFonts.has(fontConfig.fontFamily)) {
        return false;
      }

      if (failedFonts.has(fontConfig.fontFamily) && retries === 0) {
        return false;
      }

      setLoadingFonts((prev) => new Set([...prev, fontConfig.fontFamily]));

      try {
        const success = await loadFontCSS(fontConfig);

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (success) {
          setLoadedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          setFailedFonts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fontConfig.fontFamily);
            return newSet;
          });
          return true;
        } else {
          if (retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await loadFont(fontConfig, retries - 1);
          } else {
            setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
            return false;
          }
        }
      } catch (error) {
        console.error(
          `🚨 Font loading error for ${fontConfig.displayName}:`,
          error,
        );

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return await loadFont(fontConfig, retries - 1);
        } else {
          setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          return false;
        }
      }
    },
    [loadedFonts, loadingFonts, failedFonts],
  );

  // New function to preload multiple fonts
  const preloadFonts = React.useCallback(
    async (fontConfigs: any, maxConcurrent: any = 3) => {
      const chunks: any = [];
      for (let i = 0; i < fontConfigs.length; i += maxConcurrent) {
        chunks.push(fontConfigs.slice(i, i + maxConcurrent));
      }

      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map((fontConfig) => loadFont(fontConfig)),
        );
        // Small delay between chunks to prevent overwhelming the browser
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    },
    [loadFont],
  );

  return { loadFont, preloadFonts, loadedFonts, loadingFonts, failedFonts };
};

// Updated useFontTypes function with infinite loading support
export const useFontTypes = (search = "") => {
  const [fontsPreloaded, setFontsPreloaded] = useState(false);
  const { preloadFonts } = useFontLoader();
  const {
    data: fontTypes = [],
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetFonts(search);

  useEffect(() => {
    // Preload first batch of fonts immediately for better UX when data is available
    if (fontTypes.length > 0 && !fontsPreloaded) {
      const initialFonts = fontTypes.slice(0, Math.min(10, fontTypes.length));
      preloadFonts(initialFonts);
      setFontsPreloaded(true);
    }
  }, [fontTypes, fontsPreloaded, preloadFonts]);

  const preloadAllFonts = React.useCallback(async () => {
    if (fontTypes.length > 0) {
      // Preload fonts in batches when dropdown is opened
      const remainingFonts = fontTypes.slice(10);
      await preloadFonts(remainingFonts);
    }
  }, [fontTypes, preloadFonts]);

  const refetchFonts = React.useCallback(() => {
    return refetch();
  }, [refetch]);

  // Function to load more fonts when user scrolls
  const loadMoreFonts = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    fontTypes,
    isLoading,
    error: error?.message || null,
    refetchFonts,
    preloadAllFonts,
    fontsPreloaded,
    // New properties for infinite loading
    loadMoreFonts,
    hasNextPage,
    isFetchingNextPage,
  };
};

// let scriptPath =
//   "https://og-builder-cdn.outgrow.co/assets/fonts/templateFonts/fonts_varient";
// export const fontTypes = [
//   {
//     fontURL: `https://fonts.chatbotbuilder.net/1754037901922-U_lrjyFIwO1N-Stereofidelic.otf`,
//     fontFamily: `Stereofidelic`,
//     displayName: `Stereofidelic`,
//   },
//   {
//     fontURL: `${scriptPath}/Dosis.css`,
//     fontFamily: `Dosis`,
//     displayName: `Dosis`,
//   },
//   {
//     fontURL: `${scriptPath}/JosefinSans.css`,
//     fontFamily: `Josefin Sans`,
//     displayName: `Josefin Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/AlegreyaSans.css`,
//     fontFamily: `Alegreya Sans`,
//     displayName: `Alegreya Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Cicle_Gordita.css`,
//     fontFamily: `Cicle`,
//     displayName: `Cicle`,
//   },
//   {
//     fontURL: `${scriptPath}/Amaranth.css`,
//     fontFamily: `Amaranth`,
//     displayName: `Amaranth`,
//   },
//   {
//     fontURL: `${scriptPath}/clear_sans.css`,
//     fontFamily: `Clear Sans`,
//     displayName: `Clear Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/CooperHewitt.css`,
//     fontFamily: `Cooper Hewitt`,
//     displayName: `Cooper Hewitt`,
//   },
//   {
//     fontURL: `${scriptPath}/Courier_Prime_Sans.css`,
//     fontFamily: `Courier Prime Sans`,
//     displayName: `Courier Prime Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/lato.css`,
//     fontFamily: `Lato`,
//     displayName: `Lato`,
//   },
//   {
//     fontURL: `${scriptPath}/Trajan_Pro.css`,
//     fontFamily: `Trajan Pro`,
//     displayName: `Trajan Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/Orkney.css`,
//     fontFamily: `Orkney`,
//     displayName: `Orkney`,
//   },
//   {
//     fontURL: `${scriptPath}/PT_serif.css`,
//     fontFamily: `PT_Serif`,
//     displayName: `PT Serif`,
//   },
//   {
//     fontURL: `${scriptPath}/open_sans.css`,
//     fontFamily: `OpenSans`,
//     displayName: `OpenSans`,
//   },
//   {
//     fontURL: `${scriptPath}/afarat_ibn.css`,
//     fontFamily: `afarat_ibn_bladyregular`,
//     displayName: `Afarat Ibn Blady Regular`,
//   },
//   {
//     fontURL: `${scriptPath}/poppins.css`,
//     fontFamily: `Poppins`,
//     displayName: `Poppins`,
//   },
//   {
//     fontURL: `${scriptPath}/work_sans.css`,
//     fontFamily: `work_sans`,
//     displayName: `Work Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/droidserif.css`,
//     fontFamily: `droid_serif`,
//     displayName: `Droid Serif`,
//   },
//   {
//     fontURL: `${scriptPath}/Nexa.css`,
//     fontFamily: `Nexa`,
//     displayName: `Nexa`,
//   },
//   {
//     fontURL: `${scriptPath}/Karla.css`,
//     fontFamily: `Karla`,
//     displayName: `Karla`,
//   },
//   {
//     fontURL: `${scriptPath}/Lobster.css`,
//     fontFamily: `lobster`,
//     displayName: `Lobster`,
//   },
//   {
//     fontURL: `${scriptPath}/Roboto_Regular.css`,
//     fontFamily: `roboto`,
//     displayName: `Roboto Regular`,
//   },
//   {
//     fontURL: `${scriptPath}/alef.css`,
//     fontFamily: `alef`,
//     displayName: `Alef`,
//   },
//   {
//     fontURL: `${scriptPath}/comfortaa.css`,
//     fontFamily: `comfortaa`,
//     displayName: `Comfortaa`,
//   },
//   {
//     fontURL: `${scriptPath}/PT-Sans.css`,
//     fontFamily: `PT-Sans`,
//     displayName: `PT-Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/PT-mono.css`,
//     fontFamily: `PT_mono`,
//     displayName: `PT-Mono`,
//   },
//   {
//     fontURL: `${scriptPath}/ubuntu.css`,
//     fontFamily: `Ubuntu`,
//     displayName: `Ubuntu`,
//   },
//   {
//     fontURL: `${scriptPath}/Helvetica.css`,
//     fontFamily: `Helvetica`,
//     displayName: `Helvetica`,
//   },
//   {
//     fontURL: `${scriptPath}/Aller.css`,
//     fontFamily: `Aller`,
//     displayName: `Aller`,
//   },
//   {
//     fontURL: `${scriptPath}/SourceSansPro.css`,
//     fontFamily: `sourcesanspro`,
//     displayName: `SourceSansPro`,
//   },
//   {
//     fontURL: `${scriptPath}/axiforma.css`,
//     fontFamily: `axiforma`,
//     displayName: `Axiforma`,
//   },
//   {
//     fontURL: `${scriptPath}/hk_grotesk.css`,
//     fontFamily: `hkgrotesk`,
//     displayName: `HK Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/Maven_Pro.css`,
//     fontFamily: `mavenpro`,
//     displayName: `Maven Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/Baskerville.css`,
//     fontFamily: `Baskerville`,
//     displayName: `Baskerville`,
//   },
//   {
//     fontURL: `${scriptPath}/Roboto_Slab.css`,
//     fontFamily: `roboto_slab`,
//     displayName: `Roboto Slab`,
//   },
//   {
//     fontURL: `${scriptPath}/sharpsans.css`,
//     fontFamily: `sharpsans`,
//     displayName: `Sharp Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/aktivgrotesk.css`,
//     fontFamily: `aktivgrotesk`,
//     displayName: `Aktiv Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/proximanova.css`,
//     fontFamily: `proximanova`,
//     displayName: `Proxima Nova`,
//   },
//   {
//     fontURL: `${scriptPath}/Jenna_sue.css`,
//     fontFamily: `Jenna_sue`,
//     displayName: `Jenna Sue`,
//   },
//   {
//     fontURL: `${scriptPath}/SuisseWorks.css`,
//     fontFamily: `suisseworks`,
//     displayName: `Suisse Works`,
//   },
//   {
//     fontURL: `${scriptPath}/neosans.css`,
//     fontFamily: `neosans`,
//     displayName: `Neo Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/architects-daughter.css`,
//     fontFamily: `architectsdaughter`,
//     displayName: `Architects Daughter`,
//   },
//   {
//     fontURL: `${scriptPath}/freeland.css`,
//     fontFamily: `freeland`,
//     displayName: `Freeland`,
//   },
//   {
//     fontURL: `${scriptPath}/akzidenzgrotesk.css`,
//     fontFamily: `akzidenzgroteskbq`,
//     displayName: `Akzidenz Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/louize.css`,
//     fontFamily: `louize`,
//     displayName: `Louize`,
//   },
//   { fontURL: `${scriptPath}/ipa.css`, fontFamily: `ipam`, displayName: `IPA` },
//   {
//     fontURL: `${scriptPath}/apexrounded.css`,
//     fontFamily: `apexrounded`,
//     displayName: `Apex Rounded Book`,
//   },
//   {
//     fontURL: `${scriptPath}/apercu.css`,
//     fontFamily: `apercu`,
//     displayName: `Apercu`,
//   },
//   {
//     fontURL: `${scriptPath}/heebo.css`,
//     fontFamily: `heebo`,
//     displayName: `Heebo`,
//   },
//   {
//     fontURL: `${scriptPath}/muli.css`,
//     fontFamily: `Muli`,
//     displayName: `Muli`,
//   },
//   {
//     fontURL: `${scriptPath}/sarabun.css`,
//     fontFamily: `sarabun`,
//     displayName: `Sarabun`,
//   },
//   {
//     fontURL: `${scriptPath}/prompt.css`,
//     fontFamily: `prompt`,
//     displayName: `Prompt`,
//   },
//   {
//     fontURL: `${scriptPath}/nunito.css`,
//     fontFamily: `nunito`,
//     displayName: `Nunito`,
//   },
//   {
//     fontURL: `${scriptPath}/Predige.css`,
//     fontFamily: `Predige`,
//     displayName: `Predige`,
//   },
//   {
//     fontURL: `${scriptPath}/Circular.css`,
//     fontFamily: `CircularStd`,
//     displayName: `CircularStd`,
//   },
//   {
//     fontURL: `${scriptPath}/Notosanstc.css`,
//     fontFamily: `Noto Sans TC`,
//     displayName: `Noto Sans Tc`,
//   },
//   {
//     fontURL: `${scriptPath}/northwoods.css`,
//     fontFamily: `Northwoods`,
//     displayName: `Northwoods`,
//   },
//   {
//     fontURL: `${scriptPath}/Calibri.css`,
//     fontFamily: `Calibri`,
//     displayName: `Calibri`,
//   },
//   {
//     fontURL: `${scriptPath}/Guild.css`,
//     fontFamily: `GuildCircular`,
//     displayName: `GuildCircular`,
//   },
//   {
//     fontURL: `${scriptPath}/Gilroy.css`,
//     fontFamily: `Gilroy`,
//     displayName: `Gilroy`,
//   },
//   {
//     fontURL: `${scriptPath}/genesiscaresans.css`,
//     fontFamily: `genesiscaresans`,
//     displayName: `Genesis Care Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/fira_Sans.css`,
//     fontFamily: `firasans`,
//     displayName: `Fira Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/EffraPro.css`,
//     fontFamily: `effrapro`,
//     displayName: `EffraPro`,
//   },
//   {
//     fontURL: `${scriptPath}/AldaPro.css`,
//     fontFamily: `AldaPro`,
//     displayName: `AldaPro`,
//   },
//   {
//     fontURL: `${scriptPath}/glober.css`,
//     fontFamily: `glober`,
//     displayName: `Glober`,
//   },
//   {
//     fontURL: `${scriptPath}/nassim.css`,
//     fontFamily: `NassimArabic`,
//     displayName: `Nassim Arabic`,
//   },
//   {
//     fontURL: `${scriptPath}/Graphik.css`,
//     fontFamily: `Graphik`,
//     displayName: `Graphik`,
//   },
//   {
//     fontURL: `${scriptPath}/MyriadPro.css`,
//     fontFamily: `MyriadWebPro`,
//     displayName: `MyriadWebPro`,
//   },
//   {
//     fontURL: `${scriptPath}/Vazir.css`,
//     fontFamily: `vazir`,
//     displayName: `Vazir`,
//   },
//   {
//     fontURL: `${scriptPath}/BrownPro.css`,
//     fontFamily: `BrownPro`,
//     displayName: `BrownPro`,
//   },
//   {
//     fontURL: `${scriptPath}/ibmPlex.css`,
//     fontFamily: `ibmplex`,
//     displayName: `IBM Plex`,
//   },
//   {
//     fontURL: `${scriptPath}/fselliotpro.css`,
//     fontFamily: `fselliotpro`,
//     displayName: `Fselliot Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/lusitana.css`,
//     fontFamily: `lusitana`,
//     displayName: `Lusitana`,
//   },
//   {
//     fontURL: `${scriptPath}/crimson.css`,
//     fontFamily: `crimson_text`,
//     displayName: `Crimson`,
//   },
//   {
//     fontURL: `${scriptPath}/Gotham.css`,
//     fontFamily: `Gotham`,
//     displayName: `Gotham`,
//   },
//   {
//     fontURL: `${scriptPath}/azoSans.css`,
//     fontFamily: `ruiabreu_azosans`,
//     displayName: `Azo Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/assistant.css`,
//     fontFamily: `assistant`,
//     displayName: `Assistant`,
//   },
//   {
//     fontURL: `${scriptPath}/Grotesque.css`,
//     fontFamily: `Grotesque`,
//     displayName: `Basis Grotesque`,
//   },
//   {
//     fontURL: `${scriptPath}/AkkuratPro.css`,
//     fontFamily: `AkkuratPro`,
//     displayName: `AkkuratPro`,
//   },
//   {
//     fontURL: `${scriptPath}/AdobeClean.css`,
//     fontFamily: `AdobeClean`,
//     displayName: `AdobeClean`,
//   },
//   {
//     fontURL: `${scriptPath}/beausanspro.css`,
//     fontFamily: `beausanspro`,
//     displayName: `BeauSansPro`,
//   },
//   {
//     fontURL: `${scriptPath}/pk_futura_neo.css`,
//     fontFamily: `Conv_PFFuturaNeu`,
//     displayName: `PF Futura Neo`,
//   },
//   {
//     fontURL: `${scriptPath}/Amadeus.css`,
//     fontFamily: `amadeus`,
//     displayName: `Amadeus`,
//   },
//   {
//     fontURL: `${scriptPath}/DINnextLtPro.css`,
//     fontFamily: `DINNextLTPro`,
//     displayName: `DINNextLTPro`,
//   },
//   {
//     fontURL: `${scriptPath}/bank-gothic.css`,
//     fontFamily: `bank_gothic`,
//     displayName: `Bank Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/Oswald.css`,
//     fontFamily: `oswald`,
//     displayName: `Oswald`,
//   },
//   {
//     fontURL: `${scriptPath}/Playfairdisplay.css`,
//     fontFamily: `playfairdisplay`,
//     displayName: `PlayFair Display`,
//   },
//   {
//     fontURL: `${scriptPath}/futura.css`,
//     fontFamily: `futura`,
//     displayName: `Futura`,
//   },
//   {
//     fontURL: `${scriptPath}/fs_albert.css`,
//     fontFamily: `fs_albert`,
//     displayName: `FS Albert`,
//   },
//   {
//     fontURL: `${scriptPath}/Franklin-Gothic-Demi.css`,
//     fontFamily: `Franklin-Gothic-Demi`,
//     displayName: `Franklin Gothic Demi`,
//   },
//   {
//     fontURL: `${scriptPath}/Pluto.css`,
//     fontFamily: `pluto`,
//     displayName: `Pluto`,
//   },
//   {
//     fontURL: `${scriptPath}/dmsans.css`,
//     fontFamily: `dmsans`,
//     displayName: `DM Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Gravure.css`,
//     fontFamily: `Gravure`,
//     displayName: `Gravure`,
//   },
//   {
//     fontURL: `${scriptPath}/Campton.css`,
//     fontFamily: `campton`,
//     displayName: `Campton`,
//   },
//   {
//     fontURL: `${scriptPath}/Merriweather.css`,
//     fontFamily: `merriweather`,
//     displayName: `Merriweather`,
//   },
//   {
//     fontURL: `${scriptPath}/TradeGothic.css`,
//     fontFamily: `TradeGothicNextLTPro`,
//     displayName: `Trade Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/botanikastd.css`,
//     fontFamily: `botanikastd`,
//     displayName: `Botanika Std`,
//   },
//   {
//     fontURL: `${scriptPath}/AvenirNext.css`,
//     fontFamily: `AvenirNext`,
//     displayName: `AvenirNext`,
//   },
//   {
//     fontURL: `${scriptPath}/nanumgothiccoding.css`,
//     fontFamily: `nanumgothiccoding`,
//     displayName: `Nanumgothiccoding`,
//   },
//   {
//     fontURL: `${scriptPath}/DuplicateIonic.css`,
//     fontFamily: `DuplicateIonic`,
//     displayName: `Duplicate Ionic`,
//   },
//   {
//     fontURL: `${scriptPath}/AgletSans.css`,
//     fontFamily: `PYEFMS AgletSans`,
//     displayName: `AgletSans`,
//   },
//   {
//     fontURL: `${scriptPath}/44555.css`,
//     fontFamily: `Conv_44555`,
//     displayName: `Conv_44555`,
//   },
//   {
//     fontURL: `${scriptPath}/Narrow.css`,
//     fontFamily: `NarrowTypeface`,
//     displayName: `Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/Artifakt.css`,
//     fontFamily: `artifakt`,
//     displayName: `Artifakt`,
//   },
//   {
//     fontURL: `${scriptPath}/barlow.css`,
//     fontFamily: `barlowcondensed`,
//     displayName: `Barlow Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/wyoming.css`,
//     fontFamily: `wyoming`,
//     displayName: `Wyoming`,
//   },
//   {
//     fontURL: `${scriptPath}/Giovanni.css`,
//     fontFamily: `Giovanni`,
//     displayName: `Giovanni`,
//   },
//   {
//     fontURL: `${scriptPath}/flexo.css`,
//     fontFamily: `flexo`,
//     displayName: `Flexo`,
//   },
//   {
//     fontURL: `${scriptPath}/LemonMilk.css`,
//     fontFamily: `lemonmilk`,
//     displayName: `Lemon Milk`,
//   },
//   {
//     fontURL: `${scriptPath}/Arial.css`,
//     fontFamily: `arial`,
//     displayName: `Arial`,
//   },
//   {
//     fontURL: `${scriptPath}/CeraPro.css`,
//     fontFamily: `cerapro`,
//     displayName: `Cera Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/sangbleusunriseLight.css`,
//     fontFamily: `sangbleusunrise`,
//     displayName: `Sangbleu Sunrise Light`,
//   },
//   {
//     fontURL: `${scriptPath}/Klarna.css`,
//     fontFamily: `Klarna`,
//     displayName: `Klarna`,
//   },
//   {
//     fontURL: `${scriptPath}/BreeSerif.css`,
//     fontFamily: `breeserif`,
//     displayName: `BreeSerif`,
//   },
//   {
//     fontURL: `${scriptPath}/Patuaone.css`,
//     fontFamily: `patuaone`,
//     displayName: `Patua One`,
//   },
//   {
//     fontURL: `${scriptPath}/DaxCompact.css`,
//     fontFamily: `DaxCompact`,
//     displayName: `Dax Compact`,
//   },
//   {
//     fontURL: `${scriptPath}/vollkorn.css`,
//     fontFamily: `vollkorn`,
//     displayName: `Vollkorn`,
//   },
//   {
//     fontURL: `${scriptPath}/suisseintl.css`,
//     fontFamily: `suisseintl`,
//     displayName: `Suisse Intl`,
//   },
//   {
//     fontURL: `${scriptPath}/hurmegeometricsans.css`,
//     fontFamily: `hurmegeometricsans`,
//     displayName: `Hurme Geometric Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Brandon.css`,
//     fontFamily: `brandon`,
//     displayName: `Brandon`,
//   },
//   {
//     fontURL: `${scriptPath}/monumentgrotesk.css`,
//     fontFamily: `monumentgrotesk`,
//     displayName: `Monument Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/inter.css`,
//     fontFamily: `Inter`,
//     displayName: `Inter`,
//   },
//   {
//     fontURL: `${scriptPath}/LeMondeJournal.css`,
//     fontFamily: `LeMondeJournalStd`,
//     displayName: `Le Monde Journal`,
//   },
//   {
//     fontURL: `${scriptPath}/Cairo.css`,
//     fontFamily: `cairo`,
//     displayName: `Cairo`,
//   },
//   {
//     fontURL: `${scriptPath}/Lateef.css`,
//     fontFamily: `lateef`,
//     displayName: `Lateef`,
//   },
//   {
//     fontURL: `${scriptPath}/Mirza.css`,
//     fontFamily: `mirza`,
//     displayName: `Mirza`,
//   },
//   {
//     fontURL: `${scriptPath}/ArtegraSans.css`,
//     fontFamily: `Artegra_Sans`,
//     displayName: `ArtegraSans`,
//   },
//   {
//     fontURL: `${scriptPath}/TimeNewRoman.css`,
//     fontFamily: `times_new_roman`,
//     displayName: `Times New Roman`,
//   },
//   {
//     fontURL: `${scriptPath}/Articulat.css`,
//     fontFamily: `articulat`,
//     displayName: `Articulat`,
//   },
//   {
//     fontURL: `${scriptPath}/National.css`,
//     fontFamily: `National2Web`,
//     displayName: `National`,
//   },
//   {
//     fontURL: `${scriptPath}/Jost.css`,
//     fontFamily: `Conv_Jost`,
//     displayName: `Jost`,
//   },
//   {
//     fontURL: `${scriptPath}/ReplicaPro.css`,
//     fontFamily: `ReplicaPro`,
//     displayName: `Replica Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/TTNormsPro.css`,
//     fontFamily: `TTNormsPro`,
//     displayName: `TT Norms Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/InterlivingScript.css`,
//     fontFamily: `InterlivingScript`,
//     displayName: `Interliving Script`,
//   },
//   {
//     fontURL: `${scriptPath}/GT-America.css`,
//     fontFamily: `gt-america`,
//     displayName: `GT America`,
//   },
//   {
//     fontURL: `${scriptPath}/trebuchet.css`,
//     fontFamily: `trebuchet`,
//     displayName: `Trebuchet`,
//   },
//   {
//     fontURL: `${scriptPath}/WebbEllisCup.css`,
//     fontFamily: `Conv_WebbEllisCup`,
//     displayName: `WebbEllisCup`,
//   },
//   {
//     fontURL: `${scriptPath}/averta.css`,
//     fontFamily: `averta`,
//     displayName: `Averta`,
//   },
//   {
//     fontURL: `${scriptPath}/pressstart2p.css`,
//     fontFamily: `press_start_2p`,
//     displayName: `Press Start 2P`,
//   },
//   {
//     fontURL: `${scriptPath}/coresans.css`,
//     fontFamily: `coresansc`,
//     displayName: `Core Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/kanit.css`,
//     fontFamily: `kanit`,
//     displayName: `Kanit`,
//   },
//   {
//     fontURL: `${scriptPath}/lexend.css`,
//     fontFamily: `lexend`,
//     displayName: `Lexend`,
//   },
//   {
//     fontURL: `${scriptPath}/bpgarial.css`,
//     fontFamily: `bpgarial`,
//     displayName: `BPG Arial`,
//   },
//   {
//     fontURL: `${scriptPath}/ogjtd.css`,
//     fontFamily: `ogjtd`,
//     displayName: `OGJ Type Design`,
//   },
//   {
//     fontURL: `${scriptPath}/zillaslab.css`,
//     fontFamily: `zillaslab`,
//     displayName: `Zilla Slab`,
//   },
//   {
//     fontURL: `${scriptPath}/halyarddis.css`,
//     fontFamily: `halyard-display`,
//     displayName: `Halyard Display`,
//   },
//   {
//     fontURL: `${scriptPath}/para-type.css`,
//     fontFamily: `para-type`,
//     displayName: `Para Type`,
//   },
//   {
//     fontURL: `${scriptPath}/gt-walsheim.css`,
//     fontFamily: `gt-walsheim`,
//     displayName: `GT Walsheim`,
//   },
//   {
//     fontURL: `${scriptPath}/sourcehansans.css`,
//     fontFamily: `sourcehansans`,
//     displayName: `Source Hansans CN`,
//   },
//   {
//     fontURL: `${scriptPath}/museo.css`,
//     fontFamily: `museo`,
//     displayName: `Museo`,
//   },
//   {
//     fontURL: `${scriptPath}/futurapt.css`,
//     fontFamily: `futurapt`,
//     displayName: `Futura PT`,
//   },
//   {
//     fontURL: `${scriptPath}/museomoderno.css`,
//     fontFamily: `museomoderno`,
//     displayName: `Museo Moderno`,
//   },
//   {
//     fontURL: `${scriptPath}/spaceG.css`,
//     fontFamily: `spaceG`,
//     displayName: `Space Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/TTCommons.css`,
//     fontFamily: `tt_commons`,
//     displayName: `TTCommons`,
//   },
//   {
//     fontURL: `${scriptPath}/Surt.css`,
//     fontFamily: `surt`,
//     displayName: `Surt`,
//   },
//   {
//     fontURL: `${scriptPath}/LyonDisplay.css`,
//     fontFamily: `LyonDisplay`,
//     displayName: `LyonDisplay`,
//   },
//   {
//     fontURL: `${scriptPath}/varelaRound.css`,
//     fontFamily: `Varela Round`,
//     displayName: `Varela Round`,
//   },
//   {
//     fontURL: `${scriptPath}/favoritExtended.css`,
//     fontFamily: `favoritExtended`,
//     displayName: `Favorit Extended`,
//   },
//   {
//     fontURL: `${scriptPath}/sometimesTimes.css`,
//     fontFamily: `sometimesTimes`,
//     displayName: `Sometimes Times`,
//   },
//   {
//     fontURL: `${scriptPath}/porscheNext.css`,
//     fontFamily: `porscheNext`,
//     displayName: `Porsche Next`,
//   },
//   {
//     fontURL: `${scriptPath}/mercuryDisplay.css`,
//     fontFamily: `mercuryDisplay`,
//     displayName: `Mercury Display`,
//   },
//   {
//     fontURL: `${scriptPath}/aeonik.css`,
//     fontFamily: `aeonik`,
//     displayName: `Aeonik`,
//   },
//   {
//     fontURL: `${scriptPath}/frank.css`,
//     fontFamily: `frank`,
//     displayName: `Frank`,
//   },
//   {
//     fontURL: `${scriptPath}/volte.css`,
//     fontFamily: `volte`,
//     displayName: `Volte`,
//   },
//   {
//     fontURL: `${scriptPath}/prudentialM.css`,
//     fontFamily: `prudentialM`,
//     displayName: `Prudential Modern`,
//   },
//   {
//     fontURL: `${scriptPath}/sourceSerifPro.css`,
//     fontFamily: `Source Serif Pro`,
//     displayName: `Source Serif Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/peclet.css`,
//     fontFamily: `peclet`,
//     displayName: `Peclet`,
//   },
//   {
//     fontURL: `${scriptPath}/agenor.css`,
//     fontFamily: `agenor`,
//     displayName: `Agenor`,
//   },
//   {
//     fontURL: `${scriptPath}/hafferxh.css`,
//     fontFamily: `hafferxh`,
//     displayName: `Hafferxh`,
//   },
//   {
//     fontURL: `${scriptPath}/vcnudge.css`,
//     fontFamily: `vcnudge`,
//     displayName: `VCNudge`,
//   },
//   {
//     fontURL: `${scriptPath}/paracentral.css`,
//     fontFamily: `paracentral`,
//     displayName: `Para Central`,
//   },
//   {
//     fontURL: `${scriptPath}/lora.css`,
//     fontFamily: `lora`,
//     displayName: `Lora`,
//   },
//   {
//     fontURL: `${scriptPath}/underground.css`,
//     fontFamily: `underground`,
//     displayName: `P22 Underground`,
//   },
//   {
//     fontURL: `${scriptPath}/mackinac.css`,
//     fontFamily: `mackinac`,
//     displayName: `P22 Mackinac`,
//   },
//   {
//     fontURL: `${scriptPath}/obviously-narw.css`,
//     fontFamily: `obviously-narw`,
//     displayName: `Obviously Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/neuehaasunicaro.css`,
//     fontFamily: `neuehaasunicaro`,
//     displayName: `Neue Haas Unica Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/sohneBuch.css`,
//     fontFamily: `sohneBuch`,
//     displayName: `Sohne Buch`,
//   },
//   {
//     fontURL: `${scriptPath}/FlamaUltracondensed.css`,
//     fontFamily: `FlamaUltracondensed`,
//     displayName: `Flama Ultra Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/FlamaCondensed.css`,
//     fontFamily: `FlamaCondensed`,
//     displayName: `Flama Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/BasicCommercial.css`,
//     fontFamily: `BasicCommercial`,
//     displayName: `Basic Commercial`,
//   },
//   {
//     fontURL: `${scriptPath}/textanarrowalt.css`,
//     fontFamily: `textanarrowalt`,
//     displayName: `Texta Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/directorsgothic.css`,
//     fontFamily: `directorsgothic`,
//     displayName: `Directors Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/plantinMTStd.css`,
//     fontFamily: `plantinMTStd`,
//     displayName: `Plantin`,
//   },
//   {
//     fontURL: `${scriptPath}/neuehaasgrotesktext.css`,
//     fontFamily: `neuehaasgrotesktext`,
//     displayName: `Neue Haas Grotesk Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/satoshi.css`,
//     fontFamily: `Satoshi`,
//     displayName: `Satoshi`,
//   },
//   {
//     fontURL: `${scriptPath}/agrandir.css`,
//     fontFamily: `Agrandir`,
//     displayName: `Agrandir`,
//   },
//   {
//     fontURL: `${scriptPath}/BerrangerHandITCStd.css`,
//     fontFamily: `Berranger Hand ITC Std`,
//     displayName: `Berranger Hand ITC Std`,
//   },
//   {
//     fontURL: `${scriptPath}/ambit.css`,
//     fontFamily: `ambit`,
//     displayName: `Ambit`,
//   },
//   {
//     fontURL: `${scriptPath}/GowlingWlgBliss.css`,
//     fontFamily: `GowlingWlgBliss`,
//     displayName: `Gowling WLG Bliss`,
//   },
//   {
//     fontURL: `${scriptPath}/secular.css`,
//     fontFamily: `Secular One`,
//     displayName: `Secular One`,
//   },
//   {
//     fontURL: `${scriptPath}/Peak.css`,
//     fontFamily: `Peak`,
//     displayName: `Peak`,
//   },
//   {
//     fontURL: `${scriptPath}/ESKlarheitGrotesk.css`,
//     fontFamily: `ES Klarheit Grotesk`,
//     displayName: `ES Klarheit Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/generalSans.css`,
//     fontFamily: `generalSans`,
//     displayName: `General Sans`,
//   },
// ].sort((a: any, b: any) => {
//   return a.displayName < b.displayName ? -1 : 1;
// });
// export const fontWeights = ["Regular", "Medium", "Bold"];
export const fontWeights = [
  { name: "Regular", value: "400" },
  { name: "Semi-Bold", value: "500" },
  { name: "Bold", value: "700" },
];

export const fontSizes = [12, 13, 14, 15, 16, 17, 18];
export const headerFontSizes = [14, 15, 16, 17, 18, 19, 20];
export const descFontSizes = [12, 13, 14, 15];

export {
  botTone,
  language,
  usageLimit,
  replyLength,
  suggestions,
  lightColors,
  darkColors,
  themeColors,
  flags,
  AUTO_MODEL,
  models,
  openAiModels,
  claudeModels,
  geminiModels,
  miniMaxModels,
  grokModels,
  gradientColors,
  deepSeekModels,
  groqModels,
  thinkingModels,
};

export interface CustomAIConfig {
  _id: string;
  name?: string;
  model?: string;
  icon?: string;
  provider?: string;
  providerMode?: "native" | "custom-model";
  baseUrl?: string;
  [key: string]: any;
}

export interface EnhancedModel {
  name: string;
  displayName: string;
  url: string;
  isCustom?: boolean;
  configId?: string;
  config?: CustomAIConfig;
  categories?: string[];
  description?: string;
  contextWindow?: number;
  maxOutput?: number;
  provider?: string;
  uiProvider?: string;
  autoMode?: MaestroAutoMode;
}

export function normalize(s: string) {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(
      /^(openai\/|anthropic\/|google\/|groq\/|deepseek\/|grok\/|openrouter\/|minimax\/|moonshotai\/|z-ai\/)/,
      "",
    )
    .replace(/^[a-z0-9_-]+[\/:]/, "") // Strip any word/dashed-word followed by / or :
    .replace(
      /^(openai|anthropic|google|groq|deepseek|grok|openrouter|minimax|moonshotai|moonshot|z-ai|zai|glm)[\s/_:-]*/,
      "",
    )
    .replace(/^(gpt-|claude-|gemini-)/, "")
    .replace(/^(gpt|claude|gemini)[\s-_]*/, "")
    .replace(/[\s-]/g, "");
}

export function detectProvider(m: EnhancedModel, ignoreConfig = false): string {
  if (!ignoreConfig && m.config?.provider) return m.config.provider; // Prioritize custom config provider
  if (m.uiProvider) return m.uiProvider; // Allow UI grouping to differ from execution provider
  if (m.provider) return m.provider; // Use explicit provider if available

  const n = (m.displayName || m.name || "").toLowerCase();
  if (n === "auto" || n.startsWith("auto-")) return "auto";
  const isOpenAi =
    n.includes("gpt") ||
    n.includes("openai") ||
    n.startsWith("4.1") ||
    n.startsWith("4o") ||
    n.startsWith("5");
  if (isOpenAi) return "openai";

  const isAnthropic =
    n.includes("claude") ||
    n.includes("anthropic") ||
    n.includes("sonnet") ||
    n.includes("haiku") ||
    n.includes("opus");
  if (isAnthropic) return "anthropic";

  if (n.includes("openrouter") || n.includes("open router"))
    return "openrouter";
  if (n.includes("moonshot") || n.includes("kimi")) return "moonshotai";
  if (n.includes("z-ai") || n.includes("z.ai") || n.includes("glm"))
    return "z-ai";
  if (n.includes("gemini") || n.includes("google")) return "google";
  if (n.includes("minimax")) return "minimax";
  if (n.includes("deepseek")) return "deepseek";
  return "groq";
}

/* ─── Provider icon map ─────────────────────────────────────────────────── */
export const providerIcons: Record<string, string> = {
  openai:
    "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  anthropic:
    "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  google:
    "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  gemini:
    "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  groq: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
  deepseek:
    "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
  openrouter:
    "https://dlvkyia8i4zmz.cloudfront.net/b4nHxmER7jWwIQWXlzZg_svgviewer_output__6_.svg",
  moonshotai:
    "https://dlvkyia8i4zmz.cloudfront.net/U0LqTJURC62XA8nbdW1A_moonshot.svg",
  "z-ai":
    "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg",
  minimax:
    "https://dlvkyia8i4zmz.cloudfront.net/NdKB4E20RUq8WwO1F0qX_minimax_color.svg",
  grok: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
  claude:
    "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  "custom-model": "https://unpkg.com/lucide-static@latest/icons/plug.svg",
  "custommodel-comaptibility":
    "https://unpkg.com/lucide-static@latest/icons/plug.svg",
  "custommodel-compatibility":
    "https://unpkg.com/lucide-static@latest/icons/plug.svg",
};

export const autoIcon =
  "https://dlvkyia8i4zmz.cloudfront.net/47zjT8z5QPydTiEVuYfN_Frame__8___2_.svg";

export function providerIcon(provider?: string) {
  if (!provider) return providerIcons.openai;
  const key = provider.toLowerCase();
  return (
    providerIcons[key] ||
    providerIcons[key.split(/[\s-]/)[0]] ||
    providerIcons.openai
  );
}

export interface UnifiedModelSelectorProps {
  selectedModel: EnhancedModel | null;
  isCustomModel: boolean;
  configs: CustomAIConfig[];
  botData: any;
  byok: boolean | undefined;
  defaultByokConfigId?: string;
  allowedModels?: string[];
  plan?: any;
  handleModelSelect: (model: EnhancedModel) => void;
  handleConfigSelect: (configId: string) => void;
  handleModelSourceChange: (
    source: "main" | "custom",
    provider?: string,
  ) => void;
  onAddNewConfig: () => void;
  onEditConfig: (config: CustomAIConfig) => void;
  onDeleteConfig: (configId: string) => void;
  onAddModelToConfig?: (config: CustomAIConfig) => void;
  onDirectModelUpdate?: (config: CustomAIConfig) => void;
  onUpdateConfig?: (configId: string, updates: Partial<CustomAIConfig>) => void;
  onMaestroModeChange?: (mode: string) => void;
  onTryNow?: () => void;
  hideCategories?: boolean;
  hideModelQualities?: boolean;
  portalMinWidth?: number;
  closeOnSelect?: boolean;
  readOnly?: boolean;
  hideApiKeyOption?: boolean;
  canManageProviderKeys?: boolean;
}

export const botThemes = {
  light: {
    chatBgColor: "#ffffff",
    userBubbleColor: "#e5e5e5",
    botBubbleColor: "#fb5f66",
    greetingConfig: {
      headerFooterBgColor: "#eeeeee",
      typography: {
        color: "#444",
      },
    },
    typography: {
      color: "#fff",
    },
    welcomePageBgColor: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    welcomePageBtnTxtColor:
      "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
    welcomePageBtnColor:
      "linear-gradient(90deg, rgb(255, 120, 126) 0%, rgb(227, 35, 44) 100%)",
    messageBoxBgColor: "#eee",
  },
  dark: {
    chatBgColor: "#222222",
    userBubbleColor: "#404040",
    botBubbleColor: "#fb5f66",
    greetingConfig: {
      headerFooterBgColor: "#353535",
      typography: {
        color: "#fff",
      },
    },
    typography: {
      color: "#fff",
    },
    welcomePageBgColor: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    welcomePageBtnTxtColor:
      "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
    welcomePageBtnColor:
      "linear-gradient(90deg, rgb(255, 120, 126) 0%, rgb(227, 35, 44) 100%)",
    messageBoxBgColor: "#353535",
  },
};
